/*
 * Special (dynamic) prices for a stay unit — unit.pricingRules.
 * Copy of the backend's utils/stayRules.js (the server prices the booking
 * again with it) — keep them in step.
 *
 * Each night of the stay gets a price:
 *   1. a date rule covering that night  (season / holiday / festival / date_range / special_event)
 *   2. else a day rule for that weekday (weekend / weekday)
 *   3. else the base price
 * then one booking rule may change every night
 *   early_bird  : booked >= daysBefore days ahead
 *   last_minute : booked <= daysBefore days ahead
 *
 * adjustmentType fixed      -> the night costs adjustmentValue
 *                percentage -> base ± adjustmentValue %   (-10 = 10% off)
 * minimumNights: the rule only counts for stays at least that long.
 */

export const DATE_TYPES = ['season', 'holiday', 'festival', 'special_event', 'date_range'];
export const DAY_TYPES = ['weekend', 'weekday'];
export const BOOKING_TYPES = ['early_bird', 'last_minute'];
// nights: Friday + Saturday are the weekend nights
export const DEFAULT_DAYS = { weekend: [5, 6], weekday: [0, 1, 2, 3, 4] };

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const dayKey = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
};
const weekdayOf = (key) => new Date(`${key}T00:00:00Z`).getUTCDay();

function usable(rule, nights) {
  if (!rule || rule.active === false) return false;
  if (num(rule.minimumNights) > 1 && nights < num(rule.minimumNights)) return false;
  if (rule.adjustmentType === 'fixed' ? !(num(rule.adjustmentValue) > 0) : !num(rule.adjustmentValue)) return false;
  if (DATE_TYPES.includes(rule.type)) {
    const from = dayKey(rule.startDate);
    const to = dayKey(rule.endDate);
    return Boolean(from && to && from <= to);
  }
  if (BOOKING_TYPES.includes(rule.type)) return rule.daysBefore !== undefined && rule.daysBefore !== null;
  return DAY_TYPES.includes(rule.type);
}

function adjust(price, rule) {
  if (rule.adjustmentType === 'fixed') return Math.round(num(rule.adjustmentValue));
  return Math.max(0, Math.round(price * (1 + num(rule.adjustmentValue) / 100)));
}

/**
 * base: the unit's nightly price · rules: unit.pricingRules
 * stay: { nightKeys: ['2026-10-23', …], daysAhead, nights }
 * -> [{ key, price, rule }]  one per night (rule = the name that changed it, or null)
 */
export function nightPrices(base, rules, stay) {
  const keys = stay?.nightKeys || [];
  const nights = keys.length || num(stay?.nights);
  const active = (Array.isArray(rules) ? rules : []).filter((r) => usable(r, nights));

  const daysAhead = num(stay?.daysAhead);
  const booking = active.find(
    (r) =>
      (r.type === 'early_bird' && daysAhead >= num(r.daysBefore)) ||
      (r.type === 'last_minute' && daysAhead <= num(r.daysBefore)),
  );

  return keys.map((key) => {
    const dateRule = active.find(
      (r) => DATE_TYPES.includes(r.type) && dayKey(r.startDate) <= key && key <= dayKey(r.endDate),
    );
    const dayRule =
      !dateRule &&
      active.find((r) => {
        if (!DAY_TYPES.includes(r.type)) return false;
        const days = Array.isArray(r.days) && r.days.length ? r.days.map(Number) : DEFAULT_DAYS[r.type];
        return days.includes(weekdayOf(key));
      });
    const rule = dateRule || dayRule || null;
    let price = rule ? adjust(base, rule) : base;
    const names = rule ? [rule.name || rule.type] : [];
    if (booking) {
      price = adjust(price, booking);
      names.push(booking.name || booking.type);
    }
    return { key, price, rule: names.length ? names.join(' + ') : null };
  });
}


// "2026-10-23" keys for each night from check-in, and days from today
export function stayNights(checkIn, nights) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(checkIn || ''));
    if (!m || !(nights > 0)) return null;
    const start = Date.UTC(+m[1], +m[2] - 1, +m[3]);
    const now = new Date();
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    return {
        nights,
        nightKeys: Array.from({ length: nights }, (_, i) => new Date(start + i * 864e5).toISOString().slice(0, 10)),
        daysAhead: Math.round((start - today) / 864e5)
    };
}
